import axios from "axios";
import { getAuthToken } from "../context/AuthContext";

// Pas de REACT_APP_BACKEND_URL aan naar de daadwerkelijke URL van je backend API
// of gebruik een standaardwaarde als die niet is ingesteld.
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:9000";

const offerteApi = axios.create({
  baseURL: `${API_BASE_URL}api/offertes`, // Zorg dat dit overeenkomt met je backend router prefix
});

// Interceptor om het authenticatietoken aan requests toe te voegen
offerteApi.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Genereer een nieuwe offerte op basis van een offerteaanvraag.
 * @param {Object} quoteRequestData - Data voor het genereren van de offerte.
 *   Verwachte structuur: { klantNaam, klantEmail, klantTelefoon, klantBericht, selectedProducts: [{ productId, quantity }] }
 * @returns {Promise<Object>} De aangemaakte offerte.
 */
export const generateOfferteFromRequest = async (quoteRequestData) => {
  try {
    const response = await offerteApi.post("/generate", quoteRequestData);
    return response.data;
  } catch (error) {
    console.error(
      "Error bij genereren offerte:",
      error.response?.data || error.message,
    );
    throw error.response?.data || error; // Gooi de error door zodat de aanroepende functie het kan afhandelen
  }
};

/**
 * Haal alle offertes op voor het huidige bedrijf.
 * @returns {Promise<Array>} Lijst met offertes.
 */
export const getAllOffertes = async () => {
  try {
    const response = await offerteApi.get("/");
    return response.data;
  } catch (error) {
    console.error(
      "Error bij ophalen alle offertes:",
      error.response?.data || error.message,
    );
    throw error.response?.data || error;
  }
};

/**
 * Haal een specifieke offerte op basis van ID.
 * @param {string} offerteId - Het ID van de offerte.
 * @returns {Promise<Object>} De offerte details.
 */
export const getOfferteById = async (offerteId) => {
  try {
    const response = await offerteApi.get(`/${offerteId}`);
    console.log("Offerte opgehaald:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Error bij ophalen offerte ${offerteId}:`,
      error.response?.data || error.message,
    );
    throw error.response?.data || error;
  }
};

/**
 * Haal offerte-instellingen op voor het huidige bedrijf.
 * @returns {Promise<Object>} De offerte-instellingen.
 */
export const getOfferteSettings = async () => {
  try {
    const response = await offerteApi.get("/settings");
    return response.data;
  } catch (error) {
    console.error(
      "Error bij ophalen offerte instellingen:",
      error.response?.data || error.message,
    );
    throw error.response?.data || error;
  }
};

/**
 * Sla offerte-instellingen op voor het huidige bedrijf.
 * @param {Object} settingsData - De op te slane instellingendata.
 * @returns {Promise<Object>} De opgeslagen offerte-instellingen.
 */
export const saveOfferteSettings = async (settingsData) => {
  try {
    const response = await offerteApi.post("/settings", settingsData);
    return response.data;
  } catch (error) {
    console.error(
      "Error bij opslaan offerte instellingen:",
      error.response?.data || error.message,
    );
    throw error.response?.data || error;
  }
};

/**
 * Update een bestaande offerte.
 * @param {string} offerteId - Het ID van de te updaten offerte.
 * @param {Object} offerteData - De data om te updaten.
 * @returns {Promise<Object>} De geüpdatete offerte.
 */
export const updateOfferte = async (offerteId, offerteData) => {
  try {
    // Ga ervan uit dat je backend PATCH ondersteunt op /:id voor updates
    const response = await offerteApi.patch(`/${offerteId}`, offerteData);
    return response.data;
  } catch (error) {
    console.error(
      `Error bij updaten offerte ${offerteId}:`,
      error.response?.data || error.message,
    );
    throw error.response?.data || error;
  }
};

/**
 * Verwijder een offerte.
 * @param {string} offerteId - Het ID van de te verwijderen offerte.
 * @returns {Promise<void>}
 */
export const deleteOfferte = async (offerteId) => {
  try {
    // Ga ervan uit dat je backend DELETE ondersteunt op /:id
    await offerteApi.delete(`/${offerteId}`);
  } catch (error) {
    console.error(
      `Error bij verwijderen offerte ${offerteId}:`,
      error.response?.data || error.message,
    );
    throw error.response?.data || error;
  }
};

/**
 * Download een specifieke offerte als PDF.
 * @param {string} offerteId - Het ID van de offerte.
 * @returns {Promise<Blob>} De PDF data als Blob.
 */
export const downloadOffertePDF = async (offerteId) => {
  try {
    const response = await offerteApi.get(`/${offerteId}/download`, {
      responseType: "blob", // Belangrijk voor het ontvangen van bestanden
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error bij downloaden PDF voor offerte ${offerteId}:`,
      error.response?.data || error.message,
    );
    // Probeer de error blob te parsen als het JSON is voor een duidelijker bericht
    if (
      error.response &&
      error.response.data instanceof Blob &&
      error.response.data.type.includes("application/json")
    ) {
      const errJson = await error.response.data.text();
      const parsedError = JSON.parse(errJson);
      throw parsedError || error;
    }
    throw error.response?.data || error;
  }
};

/**
 * Maak een nieuwe offerte aan.
 * @param {Object} offerteData - De volledige data voor de nieuwe offerte.
 * @returns {Promise<Object>} De aangemaakte offerte.
 */
export const createOfferte = async (offerteData) => {
  try {
    // De backend verwacht mogelijk dat 'items' een JSON string is.
    // Pas dit aan op basis van je backend specificaties.
    const payload = {
      ...offerteData,
      items:
        typeof offerteData.items === "string"
          ? offerteData.items
          : JSON.stringify(offerteData.items || []),
    };
    // Het endpoint voor het aanmaken van een offerte is meestal de root van de resource, bv. POST /api/offertes
    const response = await offerteApi.post("/", payload);
    return response.data;
  } catch (error) {
    console.error(
      "Error bij aanmaken offerte:",
      error.response?.data || error.message,
    );
    // Gooi de error door zodat de aanroepende functie het kan afhandelen
    // en eventueel een specifiekere foutmelding kan tonen.
    const errToThrow = error.response?.data || error;
    if (errToThrow.message && typeof errToThrow.message === "string") {
      throw new Error(errToThrow.message);
    } else if (typeof errToThrow === "string") {
      throw new Error(errToThrow);
    }
    throw new Error("Kon offerte niet aanmaken. Onbekende fout.");
  }
};
